import { environment } from '../env';

// Background
import BlueBackground from '../assets/images/bg_bacteria_blue.jpg';
import GreenBackground from '../assets/images/bg_bacteria_green.jpg';
import OrangeBackground from '../assets/images/bg_bacteria_orange.jpg';
import PurpleBackground from '../assets/images/bg_bacteria_purple.jpg';
import RedBackground from '../assets/images/bg_bacteria_red.jpg';

// Models
import rodModel from '../assets/models/rod.fbx';
import sphereModel from '../assets/models/coccus.fbx';
import spiralModel from '../assets/models/spiroachete.fbx';
import commaModal from '../assets/models/vibrio.fbx';
import branchedModel from '../assets/models/branched.fbx';

// Placeholders (Choose a bacteria page)
import branchedPlaceholder from '../assets/placeholders/branched.png';
import commaPlaceholder from '../assets/placeholders/comma.gif';
import rodPlaceholder from '../assets/placeholders/rod.png';
import spherePlaceholder from '../assets/placeholders/spheres.png';

import RodSwimming from '../assets/images/rod-swimming.gif';

const remoteBase = 'https://s3.eu-west-2.amazonaws.com/bacteria-explorer/';
const videoBase = 'http://www.oum.ox.ac.uk/bacterialworld/bacteriaexplorer/app/videos/';
const imagesBase = 'http://www.oum.ox.ac.uk/bacterialworld/bacteriaexplorer/app/images/';
const videoDir = ( environment.localMedia ) ? videoBase : remoteBase + videoBase;
const imageDir = ( environment.localMedia ) ? imagesBase : remoteBase + imagesBase;

const bacteriaInformation = [
    {
        name: "Rod (Bacillus)",
        id: "rod",
        model: rodModel,
        placeholder: rodPlaceholder,
        cameraPos: { x: -7.3, y: 8.1, z: -13.9 }, // Initial camera position.
        backgroundImage: PurpleBackground,
        intro: "Rods are the most common shape of bacterium, but rod species come in many different lengths and widths.",
        features: [
            {
                locatorId: 'c_rod_flagellum', // Name of 3d model locator.
                cameraPos: { x: -10.44, y: 8.44, z: 11.64 }, // Best position for the camera to move to when this feature is active. {x, y, z}
                title: 'Swimming',
                content: 'Bacteria which actively swim are propelled along by a rigid helix-shaped flagellum, or many flagella. These are powered by a small ‘electric’ motor operating across the bacterial membrane which can rotate at up to 1,300 revolutions per second.',
                image: RodSwimming,
                video: null,
                //video: videoDir + 'flagellar-motor-steps-aki.mp4',
                credit: 'Akihiko Ishijima, Tohoku University, Japan'
            },
            {
                locatorId: 'c_rod_pili',
                cameraPos: { x: 9.63, y: 9.96, z: -10.96 },
                title: 'Hair-like Strands',
                content: 'Pili are tiny, hair-like strands on the surface of many bacteria. Different types are used to pull a bacterium along a surface, exchange DNA with other bacteria (sex pili), or create an immune response to pathogens.',
                image: null,
                video: null,
                credit: null
            },
            {
                locatorId: null,
                cameraPos: null,
                title: 'Surface to Volume Ratio',
                content: 'The rod shape gives lots of surface area in relation to the size of the cell, which is useful when all material, including nutrients, can only get in by diffusing through the cell\'s surface wall. The illustration shows an <em>E. coli</em> cell with this shape.',
                image: imageDir + 'e-coli-goodsell.jpg',
                video: null,
                credit: 'David Goodsell'
            },
            {
                locatorId: null,
                cameraPos: null,
                title: 'Toxic warfare',
                content: 'The image shows two <em>E. coli</em> strain colonies fighting with toxins. Detecting an attack from the red strain, cells in the lower strain colony launch a massive collective counterattack. Green colour indicates toxin production.',
                image: imageDir + 'e-coli-colonies-fighting.jpg',
                video: null,
                credit: 'Kevin Foster'
            },
            {
                locatorId: null,
                cameraPos: null,
                title: '<em>E. coli</em>',
                content: 'There are many rod-shaped species, but a very famous one is <em>Escherichia coli (E. coli)</em>. Although known for causing food poisoning, in fact most strains are completely harmless and are used intensively in research laboratories.',
                image: null,
                video: videoDir + 'rowland-swimming-e-coli.mp4',
                credit: 'Howard Berg and Linda Turner, Department of Molecular and Cellular Biology, Harvard University and The Rowland Institute'
            }
        ]
    },
    {
        name: "Sphere (Coccus)",
        id: "sphere",
        model: sphereModel,
        //placeholder: spherePlaceholder,
        backgroundImage: GreenBackground,
        intro: "The sphere, or coccus, is a common bacterial shape, and cocci tend to be very small - only about one micron across. Sometimes cocci join in chains or other configurations.",
        features: [
            {
                locatorId: null,
                cameraPos: null,
                title: 'Tiny Spheres',
                content: 'Spherical bacteria like these <em>Staphylococcus aureus</em> can\'t grow larger because their cell wall would need to be thicker to contain outward pressure from material inside the cell, but if the cell wall is too thick nutrients and chemicals cannot pass through.',
                image: imageDir + 's-aureus-storm.jpg',
                video: null,
                credit: 'Robert Turner, University of Sheffield'
            },
            {
                locatorId: null,
                cameraPos: null,
                title: 'Drifting around',
                content: 'Cocci bacteria are not able to actively move, so they either drift around in their environment or colonise surfaces.',
                image: null,
                video: null,
                credit: null
            },
            {
                locatorId: 'c_coccus_capsule',
                cameraPos: { x: 0.33, y: 18, z: -1.17 },
                title: 'Protective capsule',
                content: 'Lots of cocci have a protective capsule layer on the outside of the cell, as do some other shapes. The capsule is often linked to an ability to cause disease. It can also protect the bacterium from white blood cells. The image is of <em>Neisseria</em> cocci, with the capsule showing as a fuzzy layer outside the cells.',
                image: imageDir + 'chris-tang-group.jpg',
                video: null,
                credit: 'Christoph Tang, Dunn School of Pathology, University of Oxford'
            },
            {
                locatorId: null,
                cameraPos: null,
                title: 'Causing illness',
                content: 'This timelapse video shows a bacterial colony growing on a surface. In some species communication between cells allows the colony to detect when it is dense enough to trigger certain behaviour, such as releasing a toxin or emitting light.',
                image: imageDir + 'pseudomonas-aeruginosa-timelapse-opt.gif',
                video: null,
                credit: 'Scott Chimileski'
            },
            {
                locatorId: null,
                cameraPos: null,
                title: '<em>Prochlorococcus</em>',
                content: '<em>Prochlorococcus</em> is a tiny marine bacterium that drifts around, converting sunlight into chemical energy through photosynthesis, and releasing oxygen.',
                image: imageDir + 'prochlorococcus-chisholm-lab.jpg',
                video: null,
                credit: 'Chisholm Lab'
            }
        ]
    },
    {
        name: "Spiral (Spirochaetes)",
        id: "spiral",
        model: spiralModel,
        cameraPos: { x: 7.8, y: 7.7, z: 14.3 }, // Initial camera position.
        backgroundImage: RedBackground,
        intro: "Spirochaete bacteria are long and very thin - around 10-15 microns long, but just one micron wide - with a tight spiral shape. Many spirochaetes cause diseases.",
        features: [
            {
                locatorId: 'c_spiro_flagellum',
                cameraPos: { x: -7.84, y: 1.57, z: 16.14 },
                title: 'Swimming',
                content: 'Like other bacterial shapes, spirochaetes swim using a rigid, corkscrew-shaped structure called a flagellum, but in spirochaetes it is located inside the cell rather than outside.',
                image: null,
                video: null,
                credit: null
            },
            {
                locatorId: 'c_spiro_flagellum',
                cameraPos: { x: 7.45, y: 14.45, z: -7.75 },
                title: 'Human immune system',
                content: 'The human immune system often reacts to the flagella on bacteria, but in spirochaetes flagella are internalised, hiding them from our immune system. This protects the bacterium from an immune response.',
                image: null,
                video: null,
                credit: null
            },
            {
                locatorId: null,
                cameraPos: null,
                title: 'Viscous Environments',
                content: 'Spirochaetes like this <em>Borrelia burgdorferi</em> are able to move effectively through viscous environments. As they enter a more viscous substance the helix tightens and they speed up through the thicker material.',
                image: null,
                video: videoDir + 'burgdorferi-swiming.mp4',
                credit: 'Nyles Charon, School of Medicine, West Virginia University'
            },
            {
                locatorId: null,
                cameraPos: null,
                title: '<em>Borrelia</em>',
                content: '<em>Borrelia</em> are a group of spirochaete bacteria which cause Lyme disease, an infectious condition that is spread to humans by ticks like the one pictured here.',
                image: imageDir + '8686.jpg',
                video: null,
                credit: 'CDC/ Dr. Amanda Loftis, Dr. William Nicholson, Dr. Will Reeves, Dr. Chris Paddock'
            }
        ]
    },
    {
        name: "Comma (Vibrio) and Spiral (Spirillum)",
        id: 'comma-spiral',
        model: commaModal,
        placeholder: commaPlaceholder,
        cameraPos: { x: 7.4, y: 9.2, z: -14.0 }, // Initial camera position.
        backgroundImage: OrangeBackground,
        intro: 'Many free-living bacteria are either spiral (spirillum) or comma shaped (vibrio). Unlike the spirochaetes, whose shape allows them to move through viscous substances, these bacteria do not live in viscous environments so do not need to be so tightly coiled.',
        features: [
            {
                locatorId: 'c_vibrio_body',
                cameraPos: { x: -2.31, y: -18, z: -1.77 },
                title: 'Swimming',
                content: 'Bacteria which actively swim are propelled along by a rigid helix-shaped flagellum, or many flagella. These are powered by a small ‘electric’ motor operating across the bacterial membrane which can rotate at up to 1,300 revolutions per second.',
                image: null,
                video: videoDir + 'flagellar-motor-steps-aki.mp4',
                credit: 'Akihiko Ishijima, Tohoku University, Japan'
            },
            {
                locatorId: null,
                cameraPos: null,
                title: 'Rods to commas to spirals',
                content: 'Comma- and spiral-shaped bacteria are very similar to rods, but with a degree of twisting in their shape. It is possible to switch a single gene in a comma-shaped bacterium to turn it into a rod-shape bacterium!',
                image: null,
                video: null,
                credit: null
            },
            {
                locatorId: null,
                cameraPos: null,
                title: '<em>Caulobacter crescentus</em>',
                content: '<em>Caulobacter crescentus</em> is a comma-shaped bacterium with an unusual two-stage development. One stage attaches to a surface with a stalk which then buds off a swimming daughter cell. The swimming cell moves to a new location and attaches its stalk, after which it can bud off a new daughter cell.',
                image: imageDir + 'dividing-caulobacter.jpg',
                video: null,
                credit: 'Yves Brun, Department of Biology, Indiana University Bloomington'
            }
        ]
    },
    {
        name: "Branched",
        id: "branched",
        model: branchedModel,
        placeholder: branchedPlaceholder,
        backgroundImage: BlueBackground,
        intro: "Branched bacteria form long filament structures. Unlike other bacteria, which are individual cells, the branching filament is essentially one entity.",
        features: [
            {
                locatorId: null,
                cameraPos: null,
                title: 'Releasing spores',
                content: 'Branched bacteria have a spore stage during which they stop metabolising, create an outer wall, and disperse to a new area as a spore. These spores then begin to grow again in branched filaments. The image of <em>Streptomyces</em> shows filaments and long chains of small spores.',
                image: imageDir + 'phil-2983.jpg',
                video: null,
                credit: 'CDC/ Dr. David Berd'
            },
            {
                locatorId: null,
                cameraPos: null,
                title: 'Antibiotic production',
                content: 'Branched bacteria produce almost all of the antibiotics we use. They use the antibiotic to destroy - and eat! - other bacteria, effectively preventing other species from colonising an area. The image shows colonies of <em>Streptomyces coelicolor</em> making the blue antibiotic actinorhodin.',
                image: imageDir + 'colonies-of-streptomyces.jpg',
                video: null,
                credit: 'Tobias Kieser, John Innes Centre'
            },
            {
                locatorId: null,
                cameraPos: null,
                title: 'A single organism?',
                content: 'Unlike other chains of individual rods or cocci bacteria, branched bacterial trees are not made of individual cells. Instead, chromosomes of genetic information are located individually all along the structure.',
                image: imageDir + 'streptomyces.jpg',
                video: null,
                credit: 'Mark Buttner, John Innes Centre'
            },
            {
                locatorId: null,
                cameraPos: null,
                title: '<em>Streptomyces</em>',
                content: 'Colonies of <em>Streptomyces</em> in the soil release a molecule called geosmin, and this molecule is responsible for the smell of damp soil after a rainfall!',
                image: imageDir + 'streptomyces-in-soil.jpg',
                video: null,
                credit: 'Mark Buttner, John Innes Centre'
            }
        ]
    }
];

export default bacteriaInformation;



// WEBPACK FOOTER //
// ./src/config/bacteria-information.js